/**
 * Pitch shifting: change what note audio sounds without changing how long it
 * lasts.
 *
 * The two halves already exist. Resampling moves the pitch and the length
 * together — that is varispeed, a tape machine. WSOLA changes the length and
 * leaves the pitch alone. A pitch shift is the two composed so that the length
 * changes cancel:
 *
 *   resample the source by `f`   → pitch x f, length / f
 *   stretch that back by `f`     → pitch x f, length restored
 *
 * so a clip comes back the same number of samples long, a fifth higher.
 *
 * **The resample happens first, and this is a decision.** Doing it the other way
 * round — stretch, then resample — is the same arithmetic but has two worse
 * properties. It resamples `timeRatio * f` times as much material, and it
 * band-limits audio that has already been overlap-added, so the filter works on
 * WSOLA's output rather than on the recording. Filtering the source while it is
 * still pristine is both cheaper and cleaner.
 *
 * **Why not stretch and let the graph resample.** `AudioBufferSourceNode` has a
 * `playbackRate`, so the resample could be free. It cannot be: that rate is
 * already carrying the clip's speed, and a clip that is both stretched and
 * shifted needs the two to be different numbers. Baking the shift into the
 * render keeps `playbackRate` meaning exactly one thing.
 *
 * **Why the resampler is not linear interpolation.** Shifting up decimates, and
 * decimating without band-limiting folds everything above the new Nyquist back
 * down into the audible range as inharmonic tones — the one artefact a listener
 * names instantly as "digital". The stretcher gets away with linear
 * interpolation because it only ever uses it on fragments too short to have a
 * pitch; a shifter cannot. `resampleBandlimited` below is a windowed-sinc
 * interpolator whose cutoff follows the rate, so the same code both interpolates
 * (shifting down) and anti-aliases (shifting up).
 *
 * The shift is bounded by the domain rather than here: two octaves either way is
 * already past where WSOLA's window holds a recognisable timbre, and without
 * formant correction a voice shifted much less than that has stopped sounding
 * like the person who sang it. The clamp exists for the same reason the stretch
 * ratio's does — pretending further would be dishonest.
 *
 * Formants are a separate question and have their own module: a shift moves the
 * whole spectrum, resonances included, so a voice takes on the character of a
 * different singer unless something is done about it. `formantSemitones` says
 * where they should sit instead, and `formants.ts` pre-warps the source so that
 * the shift lands them there. It is a step in front of this pipeline rather than
 * a change to it, which is why a formant move needs no pitch shift to work: the
 * shift is then the identity and the pre-warp is the whole effect.
 *
 * Pure and synchronous, like `stretch.ts`, so it can be measured directly.
 */

import { MAX_PITCH_SEMITONES, MIN_PITCH_SEMITONES } from '@/domain/project';
import { preWarpFormants } from './formants';
import { stretchChannelsMapped, type StretchOptions } from './stretch';

/** Shifts smaller than this are not worth a render. A cent is 0.01 here. */
export const PITCH_EPSILON = 1e-3;

/**
 * Shifts are quantised to hundredths of a semitone before they become a cache
 * key, for the same reason ratios are quantised: dragging a control produces a
 * continuum, and a cent is already finer than the ear resolves.
 */
export function quantiseSemitones(semitones: number): number {
  if (!Number.isFinite(semitones)) return 0;
  const clamped = Math.min(MAX_PITCH_SEMITONES, Math.max(MIN_PITCH_SEMITONES, semitones));
  return Math.round(clamped * 100) / 100;
}

/** True when a shift is large enough to be worth rendering. */
export function isPitchShifted(semitones: number): boolean {
  return Math.abs(quantiseSemitones(semitones)) >= PITCH_EPSILON;
}

/** The frequency multiplier a semitone offset asks for. Twelve semitones is 2. */
export function pitchFactor(semitones: number): number {
  return Math.pow(2, quantiseSemitones(semitones) / 12);
}

/**
 * How many zero crossings of the sinc kernel are kept either side.
 *
 * The kernel is truncated, so this trades stopband rejection against work: each
 * output sample costs about `2 * LOBES / cutoff` multiply-adds. Twelve puts the
 * first alias image far enough down to measure as absent while keeping an
 * octave-up shift to roughly fifty taps per sample.
 */
const LOBES = 12;

function sinc(x: number): number {
  if (Math.abs(x) < 1e-9) return 1;
  const pix = Math.PI * x;
  return Math.sin(pix) / pix;
}

/** Blackman window, evaluated over -1..1. Chosen for its stopband, not its width. */
function blackman(t: number): number {
  const x = (t + 1) / 2;
  return 0.42 - 0.5 * Math.cos(2 * Math.PI * x) + 0.08 * Math.cos(4 * Math.PI * x);
}

/**
 * Resamples one channel, band-limited.
 *
 * `step` is how many input samples one output sample advances by: above 1 the
 * output is shorter and higher, below 1 longer and lower. The kernel's cutoff is
 * `min(1, 1/step)` of the *input* Nyquist, so when the output cannot represent
 * the whole input band the filter removes what would otherwise fold back, and
 * when it can the kernel is a plain interpolator.
 *
 * Reading past either end of the input contributes nothing rather than clamping
 * to the edge sample: clamping would extend the last sample into a step, and a
 * step is broadband.
 */
export function resampleBandlimited(channel: Float32Array, outLength: number, step: number): Float32Array {
  const out = new Float32Array(Math.max(0, outLength));
  const inLength = channel.length;
  if (inLength === 0 || outLength <= 0) return out;

  const cutoff = Math.min(1, 1 / Math.max(1e-9, step));
  const halfWidth = LOBES / cutoff;
  const taps = Math.ceil(halfWidth);

  for (let i = 0; i < outLength; i++) {
    const x = i * step;
    const centre = Math.floor(x);
    const frac = x - centre;

    let sum = 0;
    let weight = 0;
    for (let k = -taps + 1; k <= taps; k++) {
      const index = centre + k;
      if (index < 0 || index >= inLength) continue;
      const t = k - frac;
      const w = blackman(t / halfWidth) * cutoff * sinc(cutoff * t);
      sum += channel[index] * w;
      weight += w;
    }
    // A truncated kernel does not sum to exactly one at every fractional
    // position. Normalising by the weight actually used keeps a constant input
    // constant, which is what stops the output rippling at the sample rate.
    out[i] = weight > 1e-9 ? sum / weight : 0;
  }
  return out;
}

export interface PitchShiftOptions extends StretchOptions {
  /**
   * Where the formants sit, in semitones from where they were recorded.
   *
   * `null` — the default — lets them go wherever the pitch goes, which is what a
   * shifter does if left alone. `0` holds them exactly where they were. Any other
   * number moves them on their own: `+5` with no pitch shift at all is the same
   * note sung by a smaller person.
   *
   * Null rather than a number for "follow", because following is not a position.
   * Expressing it as `formantSemitones === pitchSemitones` would be true only
   * until the pitch was next touched.
   */
  formantSemitones?: number | null;
}

/**
 * How far the formant correction has to bend the spectrum: the pitch factor over
 * the formant factor.
 *
 * One means the two agree and there is nothing to correct — which is both the
 * default and the case the old code called "not preserving formants".
 */
export function formantWarp(semitones: number, formantSemitones: number | null | undefined): number {
  if (formantSemitones === null || formantSemitones === undefined) return 1;
  return pitchFactor(semitones) / pitchFactor(formantSemitones);
}

/** True when the formants are asked for somewhere other than where the pitch takes them. */
export function isFormantShifted(semitones: number, formantSemitones: number | null | undefined): boolean {
  return Math.abs(formantWarp(semitones, formantSemitones) - 1) > 1e-6;
}

/** Output length in samples, so callers can size buffers before rendering. */
export function pitchShiftedLength(inputLength: number, timeRatio: number): number {
  return Math.max(0, Math.round(inputLength * timeRatio));
}

/**
 * Shift `channels` by `semitones`, ending up `timeRatio` times their length.
 *
 * `timeRatio` is the length change wanted *as well as* the shift — 1 for a pure
 * pitch shift, `1 / playbackRate` for a clip that is stretched too. The two are
 * done in one pass rather than one after the other: the intermediate resample
 * has already changed the length by `1/f`, so the stretcher is asked for the
 * final length directly and the audio goes through WSOLA exactly once.
 *
 * The output length is exact by construction — the number the caller asked for,
 * not the product of two roundings — which is what lets a shifted clip occupy
 * exactly the timeline an unshifted one does.
 */
export function pitchShiftChannels(
  channels: readonly Float32Array[],
  sampleRate: number,
  semitones: number,
  timeRatio = 1,
  options: PitchShiftOptions = {},
): Float32Array[] {
  if (channels.length === 0) return [];
  const inputLength = channels[0]?.length ?? 0;
  const outLength = pitchShiftedLength(inputLength, timeRatio);
  if (outLength === 0) return channels.map(() => new Float32Array(0));

  const warp = formantWarp(semitones, options.formantSemitones);
  const bendsFormants = Math.abs(warp - 1) > 1e-6;

  if (!isPitchShifted(semitones) && !bendsFormants) {
    // Neither the note nor the body moves: this is an ordinary stretch, and going
    // through the resampler would cost a filter pass to reproduce its own input.
    return Math.abs(timeRatio - 1) < 1e-9
      ? channels.map((c) => new Float32Array(c))
      : stretchChannelsMapped(channels, sampleRate, outLength, (out) => out / timeRatio, options);
  }

  const factor = pitchFactor(semitones);

  // Step zero, when the formants are wanted anywhere other than where the pitch
  // would take them: bend the envelope in advance by exactly as much as step one
  // will bend it back. Everything after this is unchanged, which is the point —
  // and it is why a formant move with no pitch shift at all works: step one is
  // then the identity, and this step is the whole effect.
  const source = bendsFormants ? preWarpFormants(channels, sampleRate, warp) : channels;

  // Step one: play it faster or slower, taking the pitch with it.
  const shiftedLength = Math.max(1, Math.round(inputLength / factor));
  const step = inputLength > 1 ? inputLength / shiftedLength : 1;
  const shifted = source.map((channel) => resampleBandlimited(channel, shiftedLength, step));

  // Step two: put the length back — or take it wherever the clip needs it.
  if (outLength === shiftedLength) return shifted;
  const ratio = shiftedLength / outLength;
  return stretchChannelsMapped(shifted, sampleRate, outLength, (out) => out * ratio, options);
}
